import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { retrievePayerTypes, retrievePayerStatuses } from "../../../../../lib/systemData";
import CustomSelect from "../../../../common/interactive/CustomSelect";
import { caseActions } from "../../../../../store/case";
import { caseQuickUpdate } from "../../../../../lib/fetchApi";
import { notificationActions } from "../../../../../store/notifications";
import PayerStatus from "./PayerStatus";

import classes from './PayerTypes.module.css'
import { useUserTabEditPermissionDisabled } from "../../../../../lib/usersPermissions";
import PayerTypeStatusGlobalModal from "../../../../common/modal/PayerTypeStatusGlobalModal";
import BodySimpleModalV3 from "../../../../common/modal/BodySimpleModalV3";
import { filterPayerTypesForCaseType, getPayerTypeSettingLabel, isPayerTypeMismatchedForCaseType } from "../../../../../lib/script";

function resolveSystemPayerLabels(caseData) {
    if (!caseData) {
        return { typeLabel: 'Undetermined', statusLabel: 'Undetermined' }
    }

    let typeLabel = caseData.system_case_payer_type_name
    if (!typeLabel && caseData.system_case_payer_type_id) {
        typeLabel = retrievePayerTypes()?.find((item) => item.value == caseData.system_case_payer_type_id)?.label
    }
    if (!typeLabel) {
        typeLabel = caseData.system_case_payer_type_id ? 'Unknown' : 'Undetermined'
    }

    let statusLabel = caseData.system_case_payer_status_name
    if (!statusLabel && caseData.system_case_payer_status_id) {
        statusLabel = retrievePayerStatuses()?.find((item) => item.value == caseData.system_case_payer_status_id)?.label
    }
    if (!statusLabel) {
        statusLabel = caseData.system_case_payer_status_id ? 'Unknown' : 'Undetermined'
    }

    return { typeLabel, statusLabel }
}

export default function PayerTypes() {
    let userPermissionTypeStatusEdit = useUserTabEditPermissionDisabled('case_payer_type_status_edit')
    
    const dispatch = useDispatch()

    const activeCase = useSelector(state => state.case.activeCase)   
    const userToken = useSelector(state => state.user.token)

    const [selectedPayerType, setSelectedPayerType] = useState(activeCase?.case?.case_payer_type_id ? activeCase?.case?.case_payer_type_id : '')

    const [filteredOptions,setFilteredOptions] = useState(null)   

    const updateHandler = async (data) => {
        let reqBody = {
            type: data.identifier.type,
            type_id: data.identifier.type_id,
            fields: {
                [data.identifier.field] : data.data.value,
            }
        }

        if (data.data.value == null) {
            reqBody.fields.case_payer_status_id = null
        }

        let dispatchObj = {
            folder: data.identifier.folder,
            item: {
                fields: [
                    {
                        field: data.identifier.field,
                        value: data.data.value
                    },
                ]            
            }
        }
        setTimeout(() => {
            dispatch(caseActions.modifyActiveCaseMultipleObject(dispatchObj))
        }, 0)
        

        let update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)

        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        }        
        
    }

    useEffect(() => {
        setFilteredOptions(null)
        let options = retrievePayerTypes()    
        setSelectedPayerType(activeCase?.case?.case_payer_type_id ? activeCase?.case?.case_payer_type_id : '')
        if (options && activeCase && activeCase.case?.case_type_id) {
            const all_options = filterPayerTypesForCaseType(options, activeCase.case?.case_type_id)
            if (all_options && all_options.length>0) {
                setTimeout(() => {
                    setFilteredOptions([
                        {label: 'Undetermined', value: null, case_types: null},
                        ...all_options,                        
                    ])
                }, 200);                
            } else {
                setTimeout(() => {
                    setFilteredOptions([
                        {label: 'Undetermined', value: null, case_types: null, default_selected_value: true}                  
                    ])
                    setSelectedPayerType('')   
                }, 200);  
                
            }
        } else {
            setFilteredOptions([
                {label: 'Undetermined', value: null, case_types: null, default_selected_value: true}                  
            ])
        }
    }, [activeCase?.case?.case_payer_type_id, activeCase?.case?.case_type_id])

    function typesChangeHandler(params) {     
        updateHandler(params)
        setSelectedPayerType(params.data.value ? params.data.value : '')     
    }

    const hasSystemPayerMismatch = Boolean(activeCase?.case?.case_payer_type_status_mismatch)
    const { typeLabel: systemTypeLabel, statusLabel: systemStatusLabel } = resolveSystemPayerLabels(activeCase?.case)
    const payerTypes = retrievePayerTypes()
    const isPayerTypeMismatched = isPayerTypeMismatchedForCaseType(
        selectedPayerType,
        activeCase?.case?.case_type_id,
        payerTypes,
    )
    const payerTypeMismatchLabel = getPayerTypeSettingLabel(payerTypes, selectedPayerType)

    return <>
        <h3 className={classes.label}>Active Payer Type/Status</h3>
        <div className={classes.wrapper}>
            <div className={classes.container}>      
                <PayerTypeStatusGlobalModal date={activeCase.case.case_payer_type_date} user={activeCase.case?.case_payer_type_by}  mode={'type'} isDisabled={isPayerTypeMismatched}>
                    <BodySimpleModalV3 disabled={!isPayerTypeMismatched} message={`Mismatch with current Case Type, value is: ${payerTypeMismatchLabel}`}>
                        <CustomSelect
                            isDisabled={userPermissionTypeStatusEdit}
                            label={'Payer Type'}
                            optionsData={filteredOptions}
                            action={typesChangeHandler}
                            preSelectedValue={selectedPayerType}
                            identifier={{type: 'case', field: 'case_payer_type_id', folder: 'case'}}
                            undetermined={selectedPayerType == '' ? 'Undetermined' : null}
                            isMismatched={isPayerTypeMismatched}
                            fallbackSelectedOption={(value) => {
                                const found = retrievePayerTypes()?.find((item) => item.value == value)
                                if (!found) return null
                                // Display-only; keep hidden from dropdown.
                                return { ...found, isDisabled: true }
                            }}
                        />
                    </BodySimpleModalV3>
                </PayerTypeStatusGlobalModal>          

                <PayerStatus isDisabled={userPermissionTypeStatusEdit} />
            </div>
            {hasSystemPayerMismatch && (
                <p className={classes.system_mismatch}>
                    <i className="ti ti-alert-circle" aria-hidden="true" />
                    <span>System calculated: {systemTypeLabel} / {systemStatusLabel}</span>
                </p>
            )}
        </div>
    </>
}